import { isNull, isUndefined } from 'lodash';

import type { SelectOption } from '@swf/components/Inputs/components/DropDown/DropDown';
import type { Countries, Country } from '@swf/core/types/i18n';
import countriesList from '@swf/generated/countries.json';
import dialingCodesList from '@swf/generated/dialing-codes.json';

import type { PartialPhone } from './formatting';

interface DialingCode {
  code: string;
  name: string;
}

type DialingCodes = DialingCode[];

interface DialingCodeSelectOption extends SelectOption {
  numericValue: string;
}

const countries = countriesList as Countries;
const dialingCodes = dialingCodesList as DialingCodes;

export const getCountryCode = (lookupCountry: string) => {
  const country = countries.find(({ value }) => value === lookupCountry) as Country;
  const dialingCode = dialingCodes.find(({ name }) => name === country?.text);

  if (isUndefined(dialingCode?.code) || isUndefined(country?.value)) return;

  return {
    text: `+${dialingCode?.code}`,
    value: country.value
  };
};

export const getInitialValues = (lookupPhoneNumber: string, lookupCountry: string) => {
  const countryName = countries.find(({ value }) => value === lookupCountry)?.text;
  const dialingCode = dialingCodes.find(
    ({ code, name }) => name === countryName && lookupPhoneNumber?.startsWith(code)
  )?.code as string;
  const phoneNumber = lookupPhoneNumber?.replace(dialingCode, '');

  if (isUndefined(dialingCode) || isUndefined(phoneNumber)) return;

  return {
    dialingCode,
    phoneNumber
  };
};

export const getInitialOptions = (lookupPhoneNumber: string, lookupCountry: string) => {
  const country = countries.find(({ value }) => value === lookupCountry)?.text;
  const dialingCode = dialingCodeSelectOptions.find(
    ({ data: countryName }) => country === countryName
  );
  const phoneNumber = lookupPhoneNumber?.replace(dialingCode?.numericValue as string, '');

  if (isUndefined(dialingCode) || isUndefined(phoneNumber)) return;

  return {
    dialingCode: dialingCode.value as string,
    phoneNumber
  };
};

export const formatPhoneNumber = (
  lookupCountry?: string,
  lookupPhoneNumber?: Nullable<PartialPhone>
) => {
  if (
    isUndefined(lookupCountry) ||
    isUndefined(lookupPhoneNumber) ||
    isNull(lookupPhoneNumber) ||
    !lookupPhoneNumber.area
  )
    return;

  const phoneNumber = Object.values(lookupPhoneNumber).join('');
  const code = getCountryCode(lookupCountry)?.text.slice(1);

  if (isUndefined(code) || isUndefined(phoneNumber)) return;

  return `${code}${phoneNumber}`;
};

export const countriesCode = countries
  .map(({ text, value }) => {
    const dialingCode = dialingCodes.find(({ name }) => name === text) as DialingCode;

    return {
      value,
      text: dialingCode?.name,
      code: dialingCode?.code
    };
  })
  .filter(({ text }) => !isUndefined(text));

export const dialingCodeOptions = dialingCodes
  .reduce(
    (codes, currentCode) =>
      codes.find(({ code }) => code === currentCode?.code) ? codes : [...codes, currentCode],
    [dialingCodes[0]]
  )
  .map((codes) => ({
    text: `+${codes?.code}`,
    value: codes?.code
  }))
  .sort((a, b) => Number(a.value) - Number(b.value));

export const dialingCodeSelectOptions: DialingCodeSelectOption[] = dialingCodes.map(
  ({ code, name }): DialingCodeSelectOption => ({
    text: `${name} (${code})`,
    value: `${code} ${name}`,
    numericValue: code,
    data: name
  })
);

export const getCountry = (countryCode: string) =>
  countries.find(({ value }) => value === countryCode);
